/**
 * Editorial conflict-of-interest disclosure — Charter 23 T7 (KAN-150).
 *
 * Renders above the story body whenever the story names a vendor CyberFind has
 * a commercial relationship with. The disclosure text is authored server-side
 * from the vendor commercial-relationship register
 * (`@intelhub/shared` → `VENDOR_RELATIONSHIP_REGISTER`) so the wording is
 * identical on the IntelHub story page, in the editor's publish preview, and on
 * the Discourse thread. This component only presents it.
 *
 * Design decisions worth keeping:
 *
 *   - NOT dismissible, and not a modal. A disclosure a reader can close is a
 *     disclosure a reader can miss, and Principles §1.6 forbids dark patterns
 *     in both directions — nagging and hiding.
 *   - Placed above the summary, not in a footer. The point is that the reader
 *     knows about the relationship BEFORE reading our assessment of the vendor.
 *   - Uses the neutral surface tokens rather than a warning/destructive colour.
 *     A disclosed supplier relationship is not an alert; styling it as one would
 *     overstate it and, worse, train readers to dismiss the pattern.
 *   - Links to /trust so a reader can check what the policy actually commits to
 *     rather than taking the inline sentence on faith.
 */
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

export interface EditorialCoiDisclosureProps {
  /** Server-rendered disclosure sentence. Nothing renders when null/undefined. */
  disclosure: string | null | undefined
  /** Vendor display names covered by the disclosure. Used for the a11y label. */
  vendors?: string[]
  className?: string
}

export const COI_BADGE_LABEL = 'Commercial relationship disclosed'

export function EditorialCoiDisclosure({
  disclosure,
  vendors,
  className,
}: EditorialCoiDisclosureProps) {
  if (!disclosure || disclosure.trim() === '') return null

  const named = vendors && vendors.length > 0 ? `: ${vendors.join(', ')}` : ''

  return (
    <section
      aria-labelledby="editorial-coi-disclosure-heading"
      data-testid="editorial-coi-disclosure"
      className={cn(
        'rounded-md border border-border bg-muted/40 p-4 text-sm',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        {/* data-ph-no-capture must precede the dynamic aria-label: session
            replay captures attribute values verbatim, and this one names
            CyberFind's commercial counterparties. Public on the page, but a
            recording is a different distribution channel with a different
            retention story — keep it out. */}
        <span
          data-ph-no-capture
          role="status"
          aria-label={`${COI_BADGE_LABEL}${named}`}
          className="inline-flex items-center rounded-full border border-amber-500/20 bg-amber-500/10 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300"
        >
          {COI_BADGE_LABEL}
        </span>
        <h3
          id="editorial-coi-disclosure-heading"
          className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Editorial independence
        </h3>
      </div>
      <p className="mt-2 leading-6 text-foreground">{disclosure}</p>
      <p className="mt-2 text-xs text-muted-foreground">
        <Link
          to="/trust#editorial-conflicts"
          className="underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Read the CyberFind editorial independence policy
        </Link>
      </p>
    </section>
  )
}
